import {
	getTimeslotByHours,
	getTimeslotByMinutesRange,
	selectLastTimeUse,
	selectSelectedTimeslot,
	selectTimeslots,
	selectTimeslotsMap,
	useCurrentTimeslot,
	useSelectedTimeslot,
	useTimeslot,
} from 'entities/toolbar/timeslots';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import {
	formatTime,
	FormatTimeObject,
	getDiffBetweenDates,
	useTime,
} from 'shared/lib/time';
import { clearLoadedTable, resetOrderDetail } from 'entities/orders';
import { isToday } from 'date-fns';

export const useCurrentTime = () => {
	const dispatch = useAppDispatch();
	const timeslots = useAppSelector(selectTimeslots);
	const timeslotsMap = useAppSelector(selectTimeslotsMap);
	const lastTimeUsed = useAppSelector(selectLastTimeUse);
	const [currentTimeText, setCurrentTimeText] = useState<string>('');
	const { toCurrentTimeslot } = useCurrentTimeslot();
	const prevTimeText = useRef<string>(currentTimeText);
	const { formattedTime, time } = useTime();
	const { toTimeslot } = useTimeslot();

	const selectedTimeslot = useAppSelector(selectSelectedTimeslot);
	const { time: selectedTimeslotTime } = useSelectedTimeslot(selectedTimeslot);
	const isSelected = (selectedTimeslotTime === currentTimeText) && isToday(selectedTimeslot);

	const setCurrentTime = async () => {
		if (isSelected) {
			return;
		}

		document.getElementById('guests-list')?.scrollTo({
			top: 0,
			behavior: "smooth"
		});

		dispatch(clearLoadedTable());
		dispatch(resetOrderDetail());
		toCurrentTimeslot();
	};

	const autoScrollByTime = useCallback(
		async (formattedTime: FormatTimeObject, time: Date) => {
			if (formattedTime) {
				const { hours } = formattedTime;
				const notEqualsHours = currentTimeText !== `${hours}:00`;
				const slotsByHours = getTimeslotByHours(timeslotsMap, hours);
				if (slotsByHours) {
					const slotByTime = getTimeslotByMinutesRange(
						slotsByHours,
						time,
					);
					if (slotByTime) {
						const formatedTime = formatTime({
							time: slotByTime.dt_start,
						});
						if (formatedTime) {
							const timeText = formatedTime.time;
							const notEqualsTime = currentTimeText !== timeText;
							if (notEqualsTime) {
								setCurrentTimeText(timeText);
							}
						}
					} else {
						if (notEqualsHours) {
							setCurrentTimeText(`${hours}:00`);
						}
					}
				} else {
					if (notEqualsHours) {
						setCurrentTimeText(`${hours}:00`);
					}
				}
			}
		},
		[setCurrentTimeText, currentTimeText, timeslotsMap],
	);

	useEffect(() => {
		if (formattedTime && formattedTime.timeWithSec !== '00:00:00') {
			autoScrollByTime(formattedTime, time);
		}
	}, [formattedTime, time, autoScrollByTime]);

	useEffect(() => {
    if (timeslots?.length) {
      if (formattedTime) {
        const hours = formattedTime.hours;
        const diffTimes = getDiffBetweenDates(time, lastTimeUsed);
        const diffMinutes =
          diffTimes &&
          (diffTimes.minutes >= 10 || prevTimeText.current === '');
  
        if (prevTimeText.current !== currentTimeText && diffMinutes) {
          const slotsByHours = getTimeslotByHours(timeslotsMap, hours);
  
          if (slotsByHours) {
            const findedSlot = getTimeslotByMinutesRange(
              slotsByHours,
              new Date(),
            );
  
            if (findedSlot) {
              toTimeslot(findedSlot?.dt_start, { hours });
            }
          } else {
            toTimeslot(timeslots[0]?.dt_start, { hours });
          }
        }
      }
  
      prevTimeText.current = currentTimeText;
    }
	}, [
		timeslots,
		timeslotsMap,
		toTimeslot,
		currentTimeText,
		time,
		formattedTime,
		lastTimeUsed,
	]);

	return {
		setCurrentTime,
		currentTimeText,
		formattedTime,
		isSelected,
	};
};
